import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Link2, AlertCircle, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';

const SOURCE_LABELS: Record<string, string> = {
  estate_document: 'Estate',
  insurance_policy: 'Insurance',
  account: 'Account',
  manual: 'Manual',
};

export function BeneficiaryReview() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('snapshots:write');
  const [linkTarget, setLinkTarget] = useState<string | null>(null);

  const { data: unresolvedData } = useQuery({
    queryKey: ['unresolvedBeneficiaries'],
    queryFn: () => api.getUnresolvedBeneficiaries(),
  });

  const { data: membersData } = useQuery({
    queryKey: ['householdMembers'],
    queryFn: () => api.getTaxProfile(),
  });

  const linkMutation = useMutation({
    mutationFn: ({ benId, memberId }: { benId: string; memberId: string }) => {
      if (!canWrite) return Promise.reject(new Error('Permission denied'));
      return api.linkBeneficiary(benId, memberId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unresolvedBeneficiaries'] });
      queryClient.invalidateQueries({ queryKey: ['profileCompleteness'] });
      queryClient.invalidateQueries({ queryKey: ['nextActions'] });
      toast.success('Beneficiary linked to household member');
      setLinkTarget(null);
    },
    onError: () => toast.error('Failed to link beneficiary'),
  });

  const beneficiaries = unresolvedData?.beneficiaries ?? [];
  const household = [
    ...(membersData?.household?.primaryFiler ? [membersData.household.primaryFiler] : []),
    ...(membersData?.household?.spouse ? [membersData.household.spouse] : []),
    ...(membersData?.household?.dependents ?? []),
  ];

  if (beneficiaries.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-700">
            Unlinked Beneficiaries ({beneficiaries.length})
          </h3>
        </div>
        <RouterLink to="/beneficiaries" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          Review all <ChevronRight className="h-3 w-3" />
        </RouterLink>
      </div>
      <div className="divide-y divide-gray-100">
        {beneficiaries.map((ben: any) => (
          <div key={ben.id} className="px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {ben.beneficiaryDisplayName}
              </p>
              <p className="text-xs text-gray-500">
                {SOURCE_LABELS[ben.sourceType] ?? ben.sourceType}
                {ben.sourceDocumentId && (
                  <> &middot; <a href={`/documents/${ben.sourceDocumentId}`} className="text-blue-500 hover:underline">View document</a></>
                )}
              </p>
            </div>

            {linkTarget === ben.id ? (
              <div className="flex items-center gap-2">
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      linkMutation.mutate({ benId: ben.id, memberId: e.target.value });
                    }
                  }}
                >
                  <option value="" disabled>Select member...</option>
                  {household.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.relationship})</option>
                  ))}
                </select>
                <button
                  className="text-gray-400 hover:text-gray-600 text-xs"
                  onClick={() => setLinkTarget(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                onClick={() => setLinkTarget(ben.id)}
              >
                <Link2 className="h-3 w-3" />
                Link
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
